import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './clean.css';
import './styles/accessibility.css';
import './lib/i18n';
import { AuthProvider } from './providers/AuthProvider';
import { ThemeProvider } from './providers/theme-provider';
import { LanguageProvider } from './providers/LanguageProvider';
import { NetworkProvider } from './providers/NetworkProvider';
import { GoogleMapsProvider } from './providers/GoogleMapsProvider';
import { Toaster } from './components/ui/toaster';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initializeTelemetry } from './lib/observability/init-telemetry';
import { trackRuntimeMetric } from './lib/observability/runtime-metrics';

const DYNAMIC_IMPORT_RECOVERY_KEY = 'intouch-dynamic-import-recovery-ts';
const DYNAMIC_IMPORT_RECOVERY_COUNT_KEY = 'intouch-dynamic-import-recovery-count';
const DYNAMIC_IMPORT_RECOVERY_COOLDOWN_MS = 60_000;
const DYNAMIC_IMPORT_RECOVERY_MAX_ATTEMPTS = 1;
const FIRESTORE_TRANSIENT_WARN_DEDUPE_WINDOW_MS = 30_000;

const firestoreTransientWarnStats = new Map<string, { lastLoggedAt: number; suppressedCount: number }>();

const BROWSER_EXTENSION_NOISE_PATTERNS = [
  'a listener indicated an asynchronous response by returning true',
  'message channel closed before a response was received',
  'objectmultiplex - orphaned data for stream "app-init-liveness"',
  'objectmultiplex - orphaned data for stream "background-liveness"',
  'objectmultiplex - malformed chunk without name "[object object]"',
  'channel secret not available yet',
  'unable to obtain channel secret for broadcast system',
  'unable to find node id to create broadcast system',
  'broadcast channel unavailable',
  'inpage.js',
  'possible eventemitter memory leak detected',
  'permissions policy violation: compute-pressure is not allowed in this document',
  'could not establish connection. receiving end does not exist',
  'unchecked runtime.lasterror: could not establish connection',
  "cannot read properties of undefined (reading 'list')"
];

function isKnownFirestoreSnapshotPermissionNoise(args: unknown[]): boolean {
  const combined = args
    .map((arg) => {
      if (typeof arg === 'string') {
        return arg;
      }

      if (arg instanceof Error) {
        return arg.message;
      }

      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ')
    .toLowerCase();

  return (
    combined.includes('@firebase/firestore') &&
    combined.includes('snapshot listener') &&
    combined.includes('permission-denied')
  );
}

function isDynamicImportFailureMessage(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('failed to fetch dynamically imported module') ||
    normalized.includes('loading chunk') ||
    normalized.includes('dynamically imported module') ||
    normalized.includes('failed to load module script') ||
    normalized.includes('strict mime type') ||
    normalized.includes('expected a javascript-or-wasm module script') ||
    normalized.includes('unexpected token <') ||
    normalized.includes("cannot read properties of undefined (reading 'wallet')") ||
    (normalized.includes('mime type') && normalized.includes('text/html'))
  );
}

function recoverFromDynamicImportFailure(reason: unknown) {
  const message =
    typeof reason === 'string'
      ? reason
      : reason instanceof Error
        ? reason.message
        : (reason as any)?.message || (reason as any)?.payload?.message || '';

  const isPreloadEvent = reason && typeof reason === 'object' && ('type' in reason && (reason as any).type === 'vite:preloadError');

  if (!isPreloadEvent && message && !isDynamicImportFailureMessage(String(message))) return;

  const lastRecoveryTs = Number(sessionStorage.getItem(DYNAMIC_IMPORT_RECOVERY_KEY) || '0');
  const now = Date.now();

  // Protect against loops: allow at most one reload every 10 seconds to recover newly built chunks
  if (now - lastRecoveryTs > 10_000) {
    sessionStorage.setItem(DYNAMIC_IMPORT_RECOVERY_KEY, String(now));
    console.warn('[InTouch] New build chunk detected. Reloading page to sync assets...', message);
    window.location.reload();
  }
}

function isKnownBrowserExtensionAsyncNoise(reason: unknown): boolean {
  const message = String(
    typeof reason === 'string'
      ? reason
      : reason instanceof Error
        ? reason.message
        : (reason as any)?.message || ''
  ).toLowerCase();

  return message.includes('a listener indicated an asynchronous response by returning true')
    || message.includes('message channel closed before a response was received')
    || message.includes('could not establish connection. receiving end does not exist')
    || message.includes('broadcast channel unavailable')
    || message.includes('unable to obtain channel secret for broadcast system')
    || message.includes('unable to find node id to create broadcast system')
    || message.includes('channel secret not available yet')
    || message.includes('objectmultiplex')
    || message.includes('app-init-liveness')
    || message.includes('background-liveness')
    || message.includes('maxlistenersexceededwarning');
}

function isKnownBrowserExtensionConsoleNoise(args: unknown[]): boolean {
  const combined = argsToNormalizedConsoleText(args);

  // Some injected providers emit timestamp-only error lines at high frequency.
  if (
    args.length === 1
    && typeof args[0] === 'string'
    && /^\[\d{2}:\d{2}:\d{2}\]\s*error\s*$/i.test(args[0].trim())
  ) {
    return true;
  }

  return BROWSER_EXTENSION_NOISE_PATTERNS.some((pattern) => combined.includes(pattern));
}

function argsToNormalizedConsoleText(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === 'string') {
        return arg;
      }

      if (arg instanceof Error) {
        return arg.message;
      }

      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ')
    .toLowerCase();
}

function normalizeFirestoreTransientWarningKey(combined: string): string {
  return combined
    .replace(/stream\s+0x[0-9a-f]+/g, 'stream <id>')
    .replace(/\d{4}-\d{2}-\d{2}t\d{2}:\d{2}:\d{2}\.\d{3}z/g, '<timestamp>');
}

function isTransientFirestoreTransportWarning(combined: string): boolean {
  if (!combined.includes('@firebase/firestore')) return false;
  if (!combined.includes('webchannelconnection rpc')) return false;

  return (
    combined.includes("rpc 'listen' stream") ||
    combined.includes("rpc 'write' stream")
  ) && combined.includes('transport errored: uf');
}

function shouldSuppressDuplicateFirestoreWarning(combined: string): boolean {
  if (!isTransientFirestoreTransportWarning(combined)) {
    return false;
  }

  const now = Date.now();
  const key = normalizeFirestoreTransientWarningKey(combined);
  const previous = firestoreTransientWarnStats.get(key);

  if (!previous) {
    firestoreTransientWarnStats.set(key, { lastLoggedAt: now, suppressedCount: 0 });
    return false;
  }

  const withinWindow = now - previous.lastLoggedAt < FIRESTORE_TRANSIENT_WARN_DEDUPE_WINDOW_MS;
  if (withinWindow) {
    previous.suppressedCount += 1;
    firestoreTransientWarnStats.set(key, previous);
    return true;
  }

  firestoreTransientWarnStats.set(key, { lastLoggedAt: now, suppressedCount: 0 });
  return false;
}

function installBrowserExtensionNoiseFilters() {
  const originalWarn = console.warn;
  const originalError = console.error;
  let lastFirestoreSummaryAt = 0;

  console.warn = (...args: unknown[]) => {
    const combined = argsToNormalizedConsoleText(args);

    if (isKnownBrowserExtensionConsoleNoise(args)) {
      return;
    }

    if (shouldSuppressDuplicateFirestoreWarning(combined)) {
      const now = Date.now();
      if (now - lastFirestoreSummaryAt > FIRESTORE_TRANSIENT_WARN_DEDUPE_WINDOW_MS && navigator.onLine) {
        lastFirestoreSummaryAt = now;

        const suppressedTotal = Array.from(firestoreTransientWarnStats.values()).reduce(
          (acc, entry) => acc + entry.suppressedCount,
          0
        );

        if (suppressedTotal > 0) {
          originalWarn(
            `[Firestore] Suppressed ${suppressedTotal} repeated transient transport warnings in the last ${FIRESTORE_TRANSIENT_WARN_DEDUPE_WINDOW_MS / 1000}s.`
          );
        }
      }
      return;
    }

    originalWarn(...args);
  };

  console.error = (...args: unknown[]) => {
    const combined = argsToNormalizedConsoleText(args);

    if (isKnownBrowserExtensionConsoleNoise(args) || isKnownBrowserExtensionAsyncNoise(combined)) {
      return;
    }

    if (combined.includes('error connecting wallet:') && combined.includes('public_requestaccounts')) {
      return;
    }

    originalError(...args);
  };
}

installBrowserExtensionNoiseFilters();
initializeTelemetry();
trackRuntimeMetric('telemetry.bootstrap.initialize');

window.addEventListener('error', (event) => {
  const errorEvent = event as ErrorEvent;

  if (isKnownBrowserExtensionAsyncNoise(errorEvent.error || errorEvent.message)) {
    event.preventDefault();
    return;
  }

  recoverFromDynamicImportFailure(errorEvent.error || errorEvent.message);
});

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  recoverFromDynamicImportFailure(event);
});

window.addEventListener('unhandledrejection', (event) => {
  if (isKnownBrowserExtensionAsyncNoise(event.reason)) {
    event.preventDefault();
    return;
  }

  recoverFromDynamicImportFailure(event.reason);
});

// Service worker is temporarily disabled to prevent production refresh/flash loops.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
      .then(() => {
        console.log('[PWA] Service workers unregistered to stabilize runtime rendering.');
      })
      .catch((error) => {
        console.log('[PWA] Failed to unregister service workers:', error);
      });
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <BrowserRouter>
      <NetworkProvider>
        <AuthProvider>
          <LanguageProvider>
            <ThemeProvider defaultTheme="light" storageKey="intouch-theme">
              <GoogleMapsProvider>
                <App />
                <Toaster />
              </GoogleMapsProvider>
            </ThemeProvider>
          </LanguageProvider>
        </AuthProvider>
      </NetworkProvider>
    </BrowserRouter>
  </ErrorBoundary>
);